/**
 * Node FormData Types
 *
 * This file defines TypeScript types for each node's formData,
 * derived from the Zod schemas in ./schemas.
 *
 * These types provide:
 * - Autocomplete support for node formData properties
 * - Compile-time type checking
 * - Documentation sync with Zod schemas
 *
 * @see /docs/domain/nodes/ for canonical documentation
 */

import type { MessageDescriptor } from '@lingui/core'
import type { Node } from 'reactflow'
import { z } from 'zod'
import type { CompensationValueArray } from '../types'
import {
  AdditionNodeFormData,
  BonusFormDataSchema,
  BranchByUserNodeDataSchema,
  ColumnRefSchema,
  CommissionFormDataSchema,
  FilterNodeDataSchema,
  IfElseNodeDataSchema,
  MetricRefSchema,
  MultiPathNodeDataSchema,
  MultiplierNodeDataSchema,
  PaymentNodeFormDataSchema,
  TargetRefSchema
} from './schemas'
import type {
  CompensationMetricReference,
  CompensationMetricReferencePath
} from './metric-references'

// =============================================================================
// Inferred Types from Zod Schemas
// =============================================================================

/**
 * Commission node formData - straight, tiered, piecework, or field-based
 *
 * **Type Constraints:**
 * - `calculate_over_metric` and `calculate_over_column` are mutually exclusive:
 *   - `calculate_over_metric` is required for period-based rules
 *   - `calculate_over_column` is required for record-based rules
 *   - Exactly one must be provided
 * - For `type === 'tiered'`: `reference_type` is required
 *   - If `reference_type === 'target'`: `targets` is required
 *   - If `reference_type === 'metric'`: `metric` AND `metric_period` are required
 *   - If `reference_type === 'column'`: `column` is required
 * - For other types (`straight`, `piecework`, `field`): `reference_type` is optional
 *
 * @see /docs/domain/nodes/commission.md for canonical documentation
 */
export type CommissionFormData = z.infer<typeof CommissionFormDataSchema>

export type CommissionFormDataStraight = CommissionFormData & {
  type?: 'straight'
  percentage?: number
  calculate_over_column?: ColumnRef
  calculate_over_metric?: MetricRef
}
export type CommissionFormDataPiecework = CommissionFormData & {
  type?: 'piecework'
  fixed_amount?: number
}
export type CommissionFormDataField = CommissionFormData & {
  type?: 'field'
  percentage?: number
  percentage_column?: ColumnRef
  calculate_over_column?: ColumnRef
}
export type CommissionFormDataTiered = CommissionFormData & {
  type?: 'tiered'
  tiers: Tier[]
}
export type CommissionFormDataTieredTarget = CommissionFormDataTiered & {
  reference_type: 'target'
  targets: TargetRef[]
  use_target_weights?: boolean
  cap_target_achievement?: boolean
  full_period_achievement?: boolean
  /** Split a record across the tier bands it crosses instead of applying its highest rate. */
  marginal_commission_tiering?: boolean
}
export type CommissionFormDataTieredMetric = CommissionFormDataTiered & {
  reference_type: 'metric'
  metric: MetricRef
  metric_period?: 'month' | 'quarter' | 'half_year' | 'year'
  calculate_over_metric?: MetricRef
  full_period_achievement?: boolean
}
export type CommissionFormDataTieredColumn = CommissionFormDataTiered & {
  reference_type: 'column'
  column: ColumnRef
  calculate_over_column?: ColumnRef
  calculate_over_metric?: MetricRef
}

/**
 * Bonus node formData - fixed, tiered, or proportional
 *
 * **Type Constraints:**
 * - `reference_type` is required (discriminated union: 'target' | 'metric')
 * - If `reference_type === 'target'`: Either `target` OR `targets` must be provided
 * - If `reference_type === 'metric'`: `metric` is required
 * - `company_target === true` evaluates across the organization without team assignment
 * - If using `targets` with `global_target === true`: Each target may specify `teams_targets`
 *
 * @see /docs/domain/nodes/bonus.md for canonical documentation
 */
export type BonusFormData = z.infer<typeof BonusFormDataSchema>

export type BonusFormDataTiered = BonusFormData & { type: 'tiered' }
export type BonusFormDataTieredTarget = BonusFormDataTiered & {
  reference_type: 'target'
}
export type BonusFormDataTieredMetric = BonusFormDataTiered & {
  reference_type: 'metric'
}
export type BonusFormDataTieredColumn = BonusFormDataTiered & {
  reference_type: 'column'
}
export type BonusFormDataProportionalTarget = BonusFormData & {
  type: 'proportional'
  reference_type: 'target'
}

/**
 * Addition node formData - percentage or fixed
 *
 * **Type Constraints:**
 * - `type` is required (discriminated union: 'fixed' | 'percentage')
 * - If `type === 'fixed'`: `fixed_amount` is required, `reference_type` is not needed
 * - If `type === 'percentage'`: `reference_type` is required (discriminated union: 'target' | 'column' | 'metric')
 *   - If `reference_type === 'target'`: `target` is required (for period-based rules)
 *   - If `reference_type === 'column'`: `column` is required (for record-based rules)
 *   - If `reference_type === 'metric'`: `metric` is required (for period-based rules)
 *
 * @see /docs/domain/nodes/addition.md for canonical documentation
 */
export type AdditionFormData = z.infer<typeof AdditionNodeFormData>

export type AdditionFormDataFixed = AdditionFormData & { type: 'fixed' }
export type AdditionFormDataPercentage = AdditionFormData & {
  type: 'percentage'
}

export function isAdditionNode(node: AnyTypedNode): node is TypedNode<'addition'> {
  return node.type === 'addition'
}

/**
 * Multiplier node formData - target or metric based tiers
 *
 * **Type Constraints:**
 * - `reference_type` is required (discriminated union: 'target' | 'metric')
 * - If `reference_type === 'target'`: `target` is required
 * - If `reference_type === 'metric'`: `metric` is required
 * - `tiers` array is required
 *
 * @see /docs/domain/nodes/multiplier.md for canonical documentation
 */
export type MultiplierFormData = z.infer<typeof MultiplierNodeDataSchema>['formData']

// =============================================================================
// Discriminated Union Variants (for type narrowing)
// =============================================================================

export type BonusFormDataTarget = BonusFormDataTieredTarget | BonusFormDataProportionalTarget
export type BonusFormDataMetric = BonusFormDataTieredMetric

export type BonusFormDataFixed = BonusFormData & { type: 'fixed' }

export type BonusFormDataProportional = BonusFormData & {
  type: 'proportional'
}

/**
 * Extract Addition formData when type is 'fixed'
 */

export type AdditionFormDataPercentageBased = AdditionFormData & {
  type: 'percentage'
}

/**
 * Extract Addition formData when type is 'percentage' and reference_type is 'target'
 */
export type AdditionFormDataTarget = AdditionFormDataPercentageBased & {
  reference_type: 'target'
}

/**
 * Extract Addition formData when type is 'percentage' and reference_type is 'column'
 */
export type AdditionFormDataColumn = AdditionFormDataPercentageBased & {
  reference_type: 'column'
}

/**
 * Extract Addition formData when type is 'percentage' and reference_type is 'metric'
 */
export type AdditionFormDataMetric = AdditionFormDataPercentageBased & {
  reference_type: 'metric'
}

/**
 * Extract Multiplier formData when reference_type is 'target'
 */
export type MultiplierFormDataTarget = Extract<MultiplierFormData, { reference_type: 'target' }>

/**
 * Extract Multiplier formData when reference_type is 'metric'
 */
export type MultiplierFormDataMetric = Extract<MultiplierFormData, { reference_type: 'metric' }>

// =============================================================================
// Type Guards for Discriminated Unions
// =============================================================================

// --- Bonus Node Type Guards ---

export function isBonusNode(node: AnyTypedNode): node is TypedNode<'bonus'> {
  return node.type === 'bonus'
}

/**
 * Checks if bonus is tiered.
 * Narrows type to ensure `type === 'tiered'`.
 */
export function isTieredBonus(node: AnyTypedNode): node is TypedNode<'bonus'> & {
  data: TypedNodeData<'bonus'> & {
    formData: BonusFormDataTiered
  }
} {
  return isBonusNode(node) && node.data.formData.type === 'tiered'
}

export function isBonusFixedAmount(formData: BonusFormData): formData is BonusFormDataFixed {
  return formData.type === 'fixed'
}

export function isBonusProportional(
  formData: BonusFormData
): formData is BonusFormDataProportional {
  return formData.type === 'proportional'
}

export function isBonusTiered(formData: BonusFormData): formData is BonusFormDataTiered {
  return formData.type === 'tiered'
}

export function isBonusReferencingTarget(
  formData: BonusFormData
): formData is BonusFormDataTieredTarget | BonusFormDataProportionalTarget {
  return (
    (isBonusTiered(formData) || isBonusProportional(formData)) &&
    formData.reference_type === 'target'
  )
}

export function isBonusReferencingMetric(
  formData: BonusFormData
): formData is BonusFormDataTieredMetric {
  return isBonusTiered(formData) && formData.reference_type === 'metric'
}

// --- Addition Node Type Guards ---

export function isAdditionFixedAmount(
  formData: AdditionFormData
): formData is AdditionFormDataFixed {
  return formData.type === 'fixed'
}

export function isAdditionPercentageBased(
  formData: AdditionFormData
): formData is AdditionFormDataPercentageBased {
  return formData.type === 'percentage'
}

/**
 * Checks if addition uses target-based percentage.
 * Narrows type to ensure `type === 'percentage'` and `reference_type === 'target'`.
 */
export function isAdditionTargetBased(
  formData: AdditionFormData
): formData is AdditionFormDataTarget {
  return (
    isAdditionPercentageBased(formData) &&
    'reference_type' in formData &&
    formData.reference_type === 'target'
  )
}

/**
 * Checks if addition uses column-based percentage.
 * Narrows type to ensure `type === 'percentage'` and `reference_type === 'column'`.
 */
export function isAdditionColumnBased(
  formData: AdditionFormData
): formData is AdditionFormDataColumn {
  return (
    isAdditionPercentageBased(formData) &&
    'reference_type' in formData &&
    formData.reference_type === 'column'
  )
}

/**
 * Checks if addition uses metric-based percentage.
 * Narrows type to ensure `type === 'percentage'` and `reference_type === 'metric'`.
 */
export function isAdditionMetricBased(
  formData: AdditionFormData
): formData is AdditionFormDataMetric {
  return (
    isAdditionPercentageBased(formData) &&
    'reference_type' in formData &&
    formData.reference_type === 'metric'
  )
}

// --- Multiplier Node Type Guards ---

/**
 * Checks if multiplier uses target-based tiers.
 * Narrows type to ensure `reference_type === 'target'`.
 */
export function isMultiplierTargetBased(
  formData: MultiplierFormData
): formData is MultiplierFormDataTarget {
  return 'reference_type' in formData && formData.reference_type === 'target'
}

/**
 * Checks if multiplier uses metric-based tiers.
 * Narrows type to ensure `reference_type === 'metric'`.
 */
export function isMultiplierMetricBased(
  formData: MultiplierFormData
): formData is MultiplierFormDataMetric {
  return 'reference_type' in formData && formData.reference_type === 'metric'
}

export function isMultiplierNode(node: AnyTypedNode): node is TypedNode<'multiplier'> {
  return node.type === 'multiplier'
}

// =============================================================================
// Commission Node Type Guards
// =============================================================================

/**
 * Checks if commission is tiered and uses target-based tiers.
 * Narrows type to ensure `type === 'tiered'`, `reference_type === 'target'`, and `targets` exists.
 */
export function isTieredCommissionWithTargets(
  formData: CommissionFormData
): formData is CommissionFormDataTieredTarget {
  return formData.type === 'tiered' && formData.reference_type === 'target' && !!formData.targets
}

/**
 * Checks if commission is tiered and uses metric-based tiers.
 * Narrows type to ensure `type === 'tiered'`, `reference_type === 'metric'`, and `metric`/`metric_period` exist.
 */
export function isTieredCommissionWithMetric(
  formData: CommissionFormData
): formData is CommissionFormDataTieredMetric {
  return (
    formData.type === 'tiered' &&
    formData.reference_type === 'metric' &&
    !!formData.metric &&
    !!formData.metric_period
  )
}

/**
 * Checks if commission is tiered and uses column-based tiers.
 * Narrows type to ensure `type === 'tiered'`, `reference_type === 'column'`, and `column` exists.
 */
export function isTieredCommissionWithColumn(
  formData: CommissionFormData
): formData is CommissionFormDataTieredColumn {
  return formData.type === 'tiered' && formData.reference_type === 'column' && !!formData.column
}

export function isCommissionNode(node: AnyTypedNode): node is TypedNode<'commission'> {
  return node.type === 'commission'
}

/**
 * Checks if node is a tiered commission.
 * Narrows type to ensure `type === 'tiered'`.
 */
export function isTieredCommission(node: AnyTypedNode): node is TypedNode<'commission'> & {
  data: TypedNodeData<'commission'> & {
    formData: CommissionFormDataTiered
  }
} {
  return isCommissionNode(node) && node.data.formData.type === 'tiered'
}

export function isStraightCommission(
  formData: CommissionFormData
): formData is CommissionFormDataStraight {
  return formData.type === 'straight'
}

export function isPieceworkCommission(
  formData: CommissionFormData
): formData is CommissionFormDataPiecework {
  return formData.type === 'piecework'
}

export function isFieldCommission(
  formData: CommissionFormData
): formData is CommissionFormDataField {
  return formData.type === 'field'
}

/**
 * Checks if commission calculates over a metric (period-based comp rules).
 * Narrows type to ensure `calculate_over_metric` exists.
 */
export function hasCalculateOverMetric(
  formData: CommissionFormData
): formData is CommissionFormData & { calculate_over_metric: MetricRef } {
  return 'calculate_over_metric' in formData && !!formData.calculate_over_metric
}

/**
 * Checks if commission calculates over a column (record-based comp rules).
 * Narrows type to ensure `calculate_over_column` exists.
 */
export function hasCalculateOverColumn(
  formData: CommissionFormData
): formData is CommissionFormData & { calculate_over_column: ColumnRef } {
  return 'calculate_over_column' in formData && !!formData.calculate_over_column
}

/** Payment node formData - phases and installments */
export type PaymentFormData = z.infer<typeof PaymentNodeFormDataSchema>

export function isPaymentNode(node: AnyTypedNode): node is TypedNode<'payment'> {
  return node.type === 'payment'
}

/** Filter node formData - JSONLogic filters */
export type FilterFormData = z.infer<typeof FilterNodeDataSchema>['formData']

export function isFilterNode(node: AnyTypedNode): node is TypedNode<'filter'> {
  return node.type === 'filter'
}

/** If/Else node formData - JSONLogic filters */
export type IfElseFormData = z.infer<typeof IfElseNodeDataSchema>['formData']

export function isIfElseNode(node: AnyTypedNode): node is TypedNode<'if/else'> {
  return node.type === 'if/else'
}

/** Multi-Path node formData - array of JSONLogic filters */
export type MultiPathFormData = z.infer<typeof MultiPathNodeDataSchema>['formData']

export function isMultiPathNode(node: AnyTypedNode): node is TypedNode<'multi-path'> {
  return node.type === 'multi-path'
}

/** Branch By User node formData - array of JSONLogic filters */
export type BranchByUserFormData = z.infer<typeof BranchByUserNodeDataSchema>['formData']

export function isBranchByUserNode(node: AnyTypedNode): node is TypedNode<'branch-by-user'> {
  return node.type === 'branch-by-user'
}

/** Start node formData - empty, no configuration */
export type StartFormData = Record<string, never>

export function isStartNode(node: AnyTypedNode): node is TypedNode<'start'> {
  return node.type === 'start'
}

// =============================================================================
// Action Node FormData Types
// =============================================================================

/** Base action formData shared by all action types */
type BaseActionFormData = {
  type?: string
}

/** Data Check action - validates data and provides labels */
export type DataCheckFormData = BaseActionFormData & {
  action_model: 'custom-check'
  label?: string
  description?: string
}

/** Messaging action variable definition */
export type MessagingVariable = {
  type: string
  label: string
}

/** Slack Alert action - sends messages to Slack */
export type SlackAlertFormData = BaseActionFormData & {
  action_model: 'slack-alert'
  provider?: string
  variables?: MessagingVariable[]
  template_values_replaced?: string[]
}

/** Email action - sends email notifications */
export type EmailFormData = BaseActionFormData & {
  action_model: 'email'
  variables?: MessagingVariable[]
  template_values_replaced?: string[]
}

/**
 * Union of all action formData types.
 * Discriminated by `action_model` field.
 */
export type ActionFormData = DataCheckFormData | SlackAlertFormData | EmailFormData

// =============================================================================
// Node FormData Map
// =============================================================================

/**
 * Maps node IDs to their corresponding formData types.
 * Used for type-safe access to node configurations.
 */
export type NodeFormDataMap = {
  commission: CommissionFormData
  bonus: BonusFormData
  addition: AdditionFormData
  multiplier: MultiplierFormData
  payment: PaymentFormData
  filter: FilterFormData
  'if/else': IfElseFormData
  'multi-path': MultiPathFormData
  'branch-by-user': BranchByUserFormData
  start: StartFormData
  'custom-check': ActionFormData
}

/** All valid node IDs */
export type NodeId = keyof NodeFormDataMap | 'placeholder'
export type NodeIdExcludingPlaceholder = Exclude<NodeId, 'placeholder'>
export type TieredNodeId = Extract<NodeId, 'commission' | 'bonus' | 'multiplier'>
export type CalculationNodeId = Exclude<
  NodeId,
  | 'placeholder'
  | 'filter'
  | 'if/else'
  | 'multi-path'
  | 'branch-by-user'
  | 'start'
  | 'custom-check'
  | 'payment'
>

// =============================================================================
// Typed Node Data
// =============================================================================

/** Edge type options */
export type EdgeOptions = 'true' | 'false' | 'default' | 'multi-path'

/** Edge definition */
export type EdgeOptionDefinition = {
  type: EdgeOptions
}

export type ParsedValues = {
  compensationValues?: CompensationValueArray
  compensationValue?: number
  compensationValuesPeriodCandidates?: CompensationValueArray
  compensationValuePeriodCandidate?: number
}

type BaseNodeData = {
  edges?: EdgeOptionDefinition[]
  label?: MessageDescriptor
  description?: MessageDescriptor
  requiredPreviousNodes?: NodeId[]
  requireAllPreviousNodes?: boolean
  unique?: boolean
  isLast?: boolean
}

/**
 * Generic typed node data for a specific node type.
 * Provides type-safe access to node data based on the node type.
 */
export type TypedNodeData<T extends NodeId> = BaseNodeData &
  (T extends 'placeholder'
    ? Record<never, never>
    : {
        formData: T extends keyof NodeFormDataMap ? NodeFormDataMap[T] : never
        parsedValues?: ParsedValues
      })

/**
 * Discriminated union of all node data types.
 * Use this when you need to handle any node type.
 */
export type AnyTypedNodeData = TypedNodeData<NodeId>

/**
 * A fully typed React Flow Node with proper `type` and `data.formData` typing.
 * Use this instead of `Node<TypedNodeData<T>>` for proper type narrowing.
 *
 * @example
 * ```typescript
 * function processCommission(node: TypedNode<'commission'>) {
 *   // node.type is 'commission'
 *   // node.data.formData is CommissionFormData
 *   const percentage = node.data.formData.percentage
 * }
 * ```
 */
export type TypedNode<T extends NodeId> = Node<TypedNodeData<T>, T> & {
  type: T
}

/**
 * Union of all typed nodes.
 * Use this when you need to handle any node type but want proper typing.
 */
export type AnyTypedNode = TypedNode<NodeId>

/** Slack Alert action node */
export type SlackAlertActionNode = TypedNode<'custom-check'> & {
  data: TypedNodeData<'custom-check'> & {
    formData: SlackAlertFormData
  }
}

/** Email action node */
export type EmailActionNode = TypedNode<'custom-check'> & {
  data: TypedNodeData<'custom-check'> & {
    formData: EmailFormData
  }
}

/** Data Check action node */
export type DataCheckActionNode = TypedNode<'custom-check'> & {
  data: TypedNodeData<'custom-check'> & {
    formData: DataCheckFormData
  }
}

// =============================================================================
// Type Guards
// =============================================================================

export function isPlaceholderNode(node: AnyTypedNode): node is TypedNode<'placeholder'> {
  return node.type === 'placeholder'
}

export function isNotPlaceholderNode(
  node: AnyTypedNode
): node is Exclude<AnyTypedNode, TypedNode<'placeholder'>> {
  return !isPlaceholderNode(node)
}

/**
 * Type guard to check if node is of a specific type.
 * Narrows the formData type for type-safe access.
 */
export function isNodeType<T extends NodeId>(
  node: Node<AnyTypedNodeData, T>,
  nodeId: T
): node is TypedNode<T> {
  return node.type === nodeId
}

/**
 * Type guard for calculation nodes (commission, bonus, multiplier, addition)
 */
export function isCalculationNode(
  node: AnyTypedNode
): node is
  | TypedNode<'commission'>
  | TypedNode<'bonus'>
  | TypedNode<'multiplier'>
  | TypedNode<'addition'> {
  return ['commission', 'bonus', 'multiplier', 'addition'].includes(node.type!)
}

/**
 * Type guard for condition nodes (filter, if/else, multi-path, branch-by-user)
 */
export function isConditionNode(
  node: AnyTypedNode
): node is
  | TypedNode<'filter'>
  | TypedNode<'if/else'>
  | TypedNode<'multi-path'>
  | TypedNode<'branch-by-user'> {
  return ['filter', 'if/else', 'multi-path', 'branch-by-user'].includes(node.type!)
}

export type TieredMultiplierNode = TypedNode<'multiplier'> & {
  data: TypedNodeData<'multiplier'> & {
    formData: MultiplierFormDataTarget | MultiplierFormDataMetric
  }
}

export type TieredNode =
  | (TypedNode<'commission'> & {
      data: TypedNodeData<'commission'> & { formData: CommissionFormDataTiered }
    })
  | (TypedNode<'bonus'> & {
      data: TypedNodeData<'bonus'> & { formData: BonusFormDataTiered }
    })
  | TieredMultiplierNode

/**
 * Type guard for tiered nodes (nodes that use tier configurations)
 */
export function isTieredNode(node: AnyTypedNode): node is TieredNode {
  return (
    isTieredBonus(node) ||
    isTieredCommission(node) ||
    (isMultiplierNode(node) && 'tiers' in node.data.formData)
  )
}

export type TieredNodeReferencingMetric =
  | (TypedNode<'commission'> & {
      data: TypedNodeData<'commission'> & { formData: CommissionFormDataTieredMetric }
    })
  | (TypedNode<'bonus'> & {
      data: TypedNodeData<'bonus'> & { formData: BonusFormDataTieredMetric }
    })

export function isTieredNodeReferencingMetric(
  node: AnyTypedNode
): node is TieredNodeReferencingMetric {
  return (
    isTieredNode(node) &&
    'reference_type' in node.data.formData &&
    node.data.formData.reference_type === 'metric'
  )
}

export type FlowControlNode =
  | TypedNode<'filter'>
  | TypedNode<'if/else'>
  | TypedNode<'multi-path'>
  | TypedNode<'branch-by-user'>
export function isFlowControlNode(node: AnyTypedNode): node is FlowControlNode {
  return ['filter', 'if/else', 'multi-path', 'branch-by-user'].includes(node.type ?? '')
}

export type NodeWithOverwriteBehavior = TypedNode<'commission'> | TypedNode<'bonus'>
export function isNodeWithOverwriteBehavior(node: AnyTypedNode): node is NodeWithOverwriteBehavior {
  return ['commission', 'bonus'].includes(node.type!)
}

// =============================================================================
// Action Node Type Guards
// =============================================================================

export type ActionNode = DataCheckActionNode | SlackAlertActionNode | EmailActionNode

/**
 * Type guard for action nodes (custom-check)
 */
export function isActionNode(node: AnyTypedNode): node is ActionNode {
  return node.type === 'custom-check'
}

/**
 * Type guard for Data Check action nodes
 */
export function isDataCheckAction(node: AnyTypedNode): node is DataCheckActionNode {
  return isActionNode(node) && node.data.formData.action_model === 'custom-check'
}

/**
 * Type guard for Slack Alert action nodes
 */
export function isSlackAlertAction(node: AnyTypedNode): node is SlackAlertActionNode {
  return isActionNode(node) && node.data.formData.action_model === 'slack-alert'
}

/**
 * Type guard for Email action nodes
 */
export function isEmailAction(node: AnyTypedNode): node is EmailActionNode {
  return isActionNode(node) && node.data.formData.action_model === 'email'
}

// =============================================================================
// Tier Types (shared across tiered nodes)
// =============================================================================

/**
 * Tier definition used in commission, bonus, and multiplier nodes.
 * @see /docs/domain/nodes/tiers.md for canonical documentation
 */
export type Tier = {
  attainment: number
  value?: string | number
  percentage?: number
  multiplier?: number
}

// =============================================================================
// Reference Types (inferred from Zod schemas)
// =============================================================================

/**
 * Target reference used across multiple nodes
 *
 * **Type Constraints:**
 * - If `global_target === true`: `teams_targets` may specify which teams are included
 * - If `company_target === true`: no team assignment is used
 *
 * Type is inferred from TargetRefSchema which uses a discriminated union to enforce
 * that when `global_target` is true, `teams_targets` may be provided for team scoping.
 */
export type TargetRef = z.infer<typeof TargetRefSchema>

/**
 * Metric reference used across multiple nodes
 * Inferred from MetricRefSchema
 */
export type MetricRef = z.infer<typeof MetricRefSchema>

export type { CompensationMetricReference, CompensationMetricReferencePath }

/**
 * Column reference used across multiple nodes
 * Inferred from ColumnRefSchema
 */
export type ColumnRef = z.infer<typeof ColumnRefSchema>
